import { useEffect, useRef, useState } from 'react';
import { desenhoDoGabarito, type Gabarito } from './gabaritos';
import {
  ampliarEmTorno, entre, escalaQueCobre, janelaDoRecorte, travaDoDeslocamento,
} from './recorte';

/**
 * Enquadrar a foto no gabarito, antes de enviar.
 *
 * «o usuário tira a foto e já enquadra no gabarito padrão (…) para a
 *  pessoa enquadrar a foto com movimento de pinça dos dedos seguindo o
 *  gabarito com transparência.» — Humberto, 02/09/2026.
 *
 * ─── O que este arquivo faz, e o que ele recusa fazer ────────────────
 *
 * Faz: recorta a foto num QUADRADO, sempre, com a marca translúcida da
 * forma do corpo de prova por cima para a pessoa mirar. Pinça e arrasto
 * com o dedo; roda e arrasto com o mouse.
 *
 * Recusa: reduzir a imagem além do recorte. O quadro sai com a
 * resolução que a área recortada tinha no arquivo original — se a pessoa
 * enquadrou 60 % de uma foto de 4000 px, sai um quadrado de 2400 px.
 * Quem encolhe é o gerador do PDF, para o laudo caber num e-mail. O
 * mesmo motivo do `Camera.tsx`: reduzir aqui gravaria como prova uma
 * imagem que ninguém fotografou.
 *
 * Recusa também recortar PELA FORMA. O círculo é mira, não máscara: a
 * borda do anel, a etiqueta ao lado e a trinca que saiu para fora do
 * corpo de prova são exatamente o que uma foto de desmonte precisa
 * mostrar.
 *
 * ─── A conta do recorte ──────────────────────────────────────────────
 *
 * A imagem é desenhada centrada no quadro, deslocada de (dx, dy) e
 * ampliada por `e`. Um ponto (px, py) da imagem cai em
 *
 *     cx = L/2 + dx + (px − w/2)·e
 *
 * onde L é o lado do quadro na tela. Invertendo nas duas bordas sai a
 * janela lida do arquivo: lado `L/e`, começando em
 *
 *     sx = w/2 − (L/2 + dx)/e
 *
 * E a trava que impede a moldura de mostrar vazio é |dx| ≤ (w·e − L)/2.
 * O `e` mínimo é o que cobre o quadro — `max(L/w, L/h)` —, então a trava
 * nunca é impossível de satisfazer.
 */

const MIN_SAIDA = 600;
const MAX_SAIDA = 2400;
const ZOOM_MAX = 6;

/** A imagem já com a orientação do EXIF aplicada. */
async function carregar(arquivo: File): Promise<{
  fonte: CanvasImageSource; w: number; h: number; url: string;
}> {
  const url = URL.createObjectURL(arquivo);
  if (typeof createImageBitmap === 'function') {
    try {
      const bm = await createImageBitmap(arquivo, { imageOrientation: 'from-image' });
      return { fonte: bm, w: bm.width, h: bm.height, url };
    } catch { /* navegador sem a opção: cai no <img>, que já roda o EXIF */ }
  }
  const img = new Image();
  img.src = url;
  await img.decode();
  return { fonte: img, w: img.naturalWidth, h: img.naturalHeight, url };
}

export function Enquadrar({ arquivo, gabarito, titulo, aoConfirmar, aoCancelar }: {
  arquivo: File;
  gabarito: Gabarito;
  titulo: string;
  aoConfirmar(recortado: File): void;
  aoCancelar(): void;
}) {
  const quadro = useRef<HTMLDivElement>(null);
  const [img, setImg] = useState<{
    fonte: CanvasImageSource; w: number; h: number; url: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [lado, setLado] = useState(320);
  const [z, setZ] = useState(1);
  const [dx, setDx] = useState(0);
  const [dy, setDy] = useState(0);
  const [ocupado, setOcupado] = useState(false);

  /* Os dedos em curso. Ref e não estado: muda a cada movimento e
     redesenhar a tela a cada ponto atrasaria o arrasto. */
  const dedos = useRef(new Map<number, { x: number; y: number }>());
  const pinca = useRef<{ dist: number; mx: number; my: number } | null>(null);

  useEffect(() => {
    let vivo = true;
    let feito: { url: string } | null = null;
    carregar(arquivo)
      .then((r) => {
        if (!vivo) { URL.revokeObjectURL(r.url); return; }
        feito = r; setImg(r); setZ(1); setDx(0); setDy(0);
      })
      .catch(() => { if (vivo) setErro('Não consegui abrir esta imagem.'); });
    return () => {
      vivo = false;
      if (feito) URL.revokeObjectURL(feito.url);
    };
  }, [arquivo]);

  useEffect(() => {
    const medir = () => {
      const l = quadro.current?.clientWidth;
      if (l) setLado(l);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [img]);

  const base = img ? escalaQueCobre(lado, img.w, img.h) : 1;
  const e = base * z;

  /** A trava: a imagem nunca deixa a moldura mostrar vazio. */
  const travar = (nz: number, ndx: number, ndy: number) => {
    if (!img) return;
    const ne = base * nz;
    const lx = travaDoDeslocamento(lado, img.w, ne);
    const ly = travaDoDeslocamento(lado, img.h, ne);
    setZ(nz);
    setDx(entre(ndx, -lx, lx));
    setDy(entre(ndy, -ly, ly));
  };

  const ampliar = (k: number, mx: number, my: number) => {
    const a = ampliarEmTorno(lado, z, dx, dy, k, mx, my, ZOOM_MAX);
    travar(a.z, a.dx, a.dy);
  };

  const local = (ev: { clientX: number; clientY: number }) => {
    const r = quadro.current?.getBoundingClientRect();
    return { x: ev.clientX - (r?.left ?? 0), y: ev.clientY - (r?.top ?? 0) };
  };

  const desceu = (ev: React.PointerEvent) => {
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
    dedos.current.set(ev.pointerId, local(ev));
    pinca.current = null;
  };

  const moveu = (ev: React.PointerEvent) => {
    if (!dedos.current.has(ev.pointerId) || !img) return;
    const antes = dedos.current.get(ev.pointerId)!;
    const agora = local(ev);
    dedos.current.set(ev.pointerId, agora);

    if (dedos.current.size >= 2) {
      const [a, b] = [...dedos.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      const p = pinca.current;
      if (p && p.dist > 0) {
        /* Pinça: amplia pela razão das distâncias E arrasta pelo meio
           dos dois dedos — sem a segunda parte a imagem escorrega
           quando a mão inteira se move sem abrir. */
        const a = ampliarEmTorno(lado, z, dx, dy, dist / p.dist, mx, my, ZOOM_MAX);
        travar(a.z, a.dx + (mx - p.mx), a.dy + (my - p.my));
      }
      pinca.current = { dist, mx, my };
      return;
    }
    travar(z, dx + (agora.x - antes.x), dy + (agora.y - antes.y));
  };

  const subiu = (ev: React.PointerEvent) => {
    dedos.current.delete(ev.pointerId);
    if (dedos.current.size < 2) pinca.current = null;
  };

  const recortar = () => {
    if (!img || ocupado) return;
    setOcupado(true);
    const { sx, sy, tamanho: janela } = janelaDoRecorte(
      { lado, w: img.w, h: img.h, escala: e, dx, dy });
    const saida = Math.round(entre(janela, MIN_SAIDA, MAX_SAIDA));

    const tela = document.createElement('canvas');
    tela.width = saida; tela.height = saida;
    const ctx = tela.getContext('2d');
    if (!ctx) { setOcupado(false); return; }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img.fonte, sx, sy, janela, janela, 0, 0, saida, saida);
    tela.toBlob((b) => {
      setOcupado(false);
      if (!b) { setErro('Não consegui recortar esta imagem.'); return; }
      const nome = arquivo.name.replace(/\.[^.]+$/, '') + '-enquadrada.jpg';
      aoConfirmar(new File([b], nome, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  const d = desenhoDoGabarito(gabarito.forma);

  return (
    <div className="camera-fundo" role="dialog" aria-modal="true" aria-label={titulo}>
      <div className="camera-caixa enquadrar">
        <div className="camera-topo">
          <b>{titulo}</b>
          <button type="button" className="botao discreto" onClick={aoCancelar}>
            Cancelar
          </button>
        </div>

        {erro && <div className="aviso erro" role="alert">{erro}</div>}

        <div
          ref={quadro}
          className="enquadrar-quadro"
          onPointerDown={desceu}
          onPointerMove={moveu}
          onPointerUp={subiu}
          onPointerCancel={subiu}
          onWheel={(ev) => {
            const p = local(ev);
            ampliar(ev.deltaY < 0 ? 1.12 : 1 / 1.12, p.x, p.y);
          }}
        >
          {img && (
            <img
              src={img.url}
              alt=""
              draggable={false}
              className="enquadrar-foto"
              style={{
                width: img.w, height: img.h,
                transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${e})`,
              }}
            />
          )}

          {/*
            A máscara e o traço.

            `fill-rule: evenodd` faz o retângulo inteiro escurecer MENOS a
            forma — é o mesmo caminho desenhado duas vezes, e não duas
            camadas empilhadas, para não haver costura visível na borda.
          */}
          <svg className="enquadrar-marca" viewBox="0 0 100 100" aria-hidden="true">
            {d ? (
              <>
                <path d={`M0,0 H100 V100 H0 Z ${d.vazado}`} fillRule="evenodd"
                      className="marca-sombra" />
                {d.apoio && <path d={d.apoio} className="marca-apoio" />}
                <path d={d.traco} className="marca-traco" />
              </>
            ) : (
              <path d="M0,0 H100 V100 H0 Z" className="marca-vazia" />
            )}
          </svg>
        </div>

        <div className="enquadrar-controles">
          <p className="sub">{gabarito.dica} Arraste para mover, pinça para ampliar.</p>
          <input
            type="range" min={100} max={ZOOM_MAX * 100} value={Math.round(z * 100)}
            aria-label="Ampliação"
            onChange={(ev) =>
              ampliar(Number(ev.target.value) / 100 / z, lado / 2, lado / 2)}
          />
        </div>

        <div className="camera-acoes">
          <button
            type="button" className="botao secundario"
            onClick={() => { setZ(1); setDx(0); setDy(0); }}
          >
            Recomeçar
          </button>
          <button
            type="button" className="botao grande"
            disabled={!img || ocupado}
            onClick={recortar}
          >
            {ocupado ? 'Recortando…' : 'Usar esta foto'}
          </button>
        </div>
      </div>
    </div>
  );
}
